import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import paymentMethodService from '../../services/paymentMethodService';
import paymentTermService from '../../services/paymentTermService';
import itemSetsService from '../../services/itemSetsService';

// Helper function to filter out unwanted fields from suppliers
const filterSupplierData = (suppliers) => {
  return suppliers.map(supplier => {
    const { bankInfo, ...filteredSupplier } = supplier;
    return filteredSupplier;
  });
};

// Helper function to filter out unwanted fields from customers
const filterCustomerData = (customers) => {
  return customers.map(customer => {
    const { companyId, ...filteredCustomer } = customer;
    return filteredCustomer;
  });
};

// Helper function to remove duplicates based on _id or id
const removeDuplicates = (customers) => {
  const seen = new Set();
  return customers.filter(customer => {
    const customerId = customer._id || customer.id;
    if (seen.has(customerId)) {
      return false;
    }
    seen.add(customerId);
    return true;
  });
};

// Helper function to filter out unwanted fields from employees
const filterEmployeeData = (employees) => {
  return employees.map(employee => {
    const { featuresAvailed, loginId, ...filteredEmployee } = employee;
    return filteredEmployee;
  });
};

// Async thunk to fetch master data
export const fetchMasterData = createAsyncThunk(
  'masterData/fetchMasterData',
  async (options = {}, { rejectWithValue }) => {
    try {
      const itemLimit = options.itemLimit ?? 1000;
      const customerLimit = options.customerLimit ?? 1000;
      let paymentMethodsFailed = false;
      let paymentTermsFailed = false;

      // Load small lookups first so they are not crowded out by heavy list queries.
      const [paymentMethodsResponse, paymentTermsResponse, categoriesResponse, itemSetsResponse] = await Promise.all([
        paymentMethodService.getPaymentMethods().catch((error) => {
          paymentMethodsFailed = true;
          console.warn('Failed to fetch payment methods:', error);
          return [];
        }),
        paymentTermService.getPaymentTerms().catch((error) => {
          paymentTermsFailed = true;
          console.warn('Failed to fetch payment terms:', error);
          return [];
        }),
        api.get('/inventory-categories').catch(() => ({ data: { flat: [], tree: [] } })),
        itemSetsService.getItemSets().catch((error) => {
          console.warn('Failed to fetch item sets:', error);
          return [];
        }),
      ]);

      const [inventoryResponse, suppliersResponse, customersResponse, employeesResponse] = await Promise.all([
        api.get(`/inventory?limit=${itemLimit}&skip=0`),
        api.get('/suppliers'),
        api.get(`/customers?limit=${customerLimit}&skip=0`),
        api.get('/employees'),
      ]);

      // Filter customer data and remove duplicates from the fresh data
      const customers = filterCustomerData(customersResponse.data?.customers || []);
      const uniqueCustomers = removeDuplicates(customers);

      // Handle payment methods - initialize defaults if none exist (not if the fetch failed)
      let paymentMethods = paymentMethodsResponse || [];
      if (!paymentMethodsFailed && (!paymentMethods || paymentMethods.length === 0)) {
        try {
          await paymentMethodService.initializeDefaults();
          paymentMethods = await paymentMethodService.getPaymentMethods();
        } catch (error) {
          console.warn('Failed to initialize default payment methods:', error);
          paymentMethods = [];
        }
      }

      // Handle payment terms - initialize defaults if none exist (not if the fetch failed)
      let paymentTerms = paymentTermsResponse || [];
      if (!paymentTermsFailed && (!paymentTerms || paymentTerms.length === 0)) {
        try {
          await paymentTermService.initializeDefaults();
          paymentTerms = await paymentTermService.getPaymentTerms();
        } catch (error) {
          console.warn('Failed to initialize default payment terms:', error);
          paymentTerms = [];
        }
      }

      // Handle inventory response - it may be an array or an object with items property (due to pagination)
      const inventoryData = inventoryResponse?.data || [];
      const inventoryItems = Array.isArray(inventoryData) 
        ? inventoryData 
        : (inventoryData.items || []);

      // Handle categories response
      const categoriesData = categoriesResponse?.data || { flat: [], tree: [] };

      // Handle item sets response - ensure it's always a plain array
      let itemSets = [];
      if (itemSetsResponse) {
        try {
          let rawItemSets = [];
          if (Array.isArray(itemSetsResponse)) {
            rawItemSets = itemSetsResponse;
          } else if (itemSetsResponse?.data && Array.isArray(itemSetsResponse.data)) {
            rawItemSets = itemSetsResponse.data;
          } else if (itemSetsResponse?.itemSets && Array.isArray(itemSetsResponse.itemSets)) {
            rawItemSets = itemSetsResponse.itemSets;
          }
          
          // Serialize to ensure plain objects (remove functions, circular refs, etc.)
          if (rawItemSets.length > 0) {
            itemSets = JSON.parse(JSON.stringify(rawItemSets));
          }
        } catch (e) {
          console.warn('Error processing item sets:', e);
          itemSets = [];
        }
      }

      return {
        items: inventoryItems,
        suppliers: filterSupplierData(suppliersResponse.data || []),
        customers: uniqueCustomers,
        employees: filterEmployeeData(employeesResponse.data?.employees || []),
        paymentMethods: paymentMethods,
        paymentTerms: paymentTerms,
        categories: categoriesData?.flat || [],
        categoryTree: categoriesData?.tree || [],
        itemSets: itemSets
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch master data');
    }
  },
  {
    condition: (arg, { getState }) => {
      const force = arg === true || (arg && arg.force);
      const { loading, lastFetchedAt } = getState().masterData || {};
      if (loading) return false;
      if (!force && lastFetchedAt && Date.now() - lastFetchedAt < 30000) return false;
      return true;
    },
  }
);

const initialState = {
  items: [],
  suppliers: [],
  customers: [],
  employees: [],
  paymentMethods: [],
  paymentTerms: [],
  categories: [],
  categoryTree: [],
  itemSets: [],
  loading: false,
  error: null,
  lastFetchedAt: null,
};

const masterDataSlice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {
    clearMasterData: (state) => {
      state.items = [];
      state.suppliers = [];
      state.customers = [];
      state.employees = [];
      state.paymentMethods = [];
      state.paymentTerms = [];
      state.categories = [];
      state.categoryTree = [];
      state.itemSets = [];
      state.error = null;
    },
    addItem: (state, action) => {
      state.items.push(action.payload);
    },
    updateItem: (state, action) => {
      const p = action.payload;
      const pid = p._id ?? p.id;
      const index = state.items.findIndex((item) => (item._id ?? item.id) === pid);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    setItems: (state, action) => {
      state.items = action.payload;
    },
    removeItem: (state, action) => {
      state.items = state.items.filter(item => item._id !== action.payload);
    },
    addSupplier: (state, action) => {
      state.suppliers.push(filterSupplierData([action.payload])[0]);
    },
    updateSupplier: (state, action) => {
      const index = state.suppliers.findIndex(supplier => supplier._id === action.payload._id);
      if (index !== -1) {
        state.suppliers[index] = filterSupplierData([action.payload])[0];
      }
    },
    removeSupplier: (state, action) => {
      state.suppliers = state.suppliers.filter(supplier => supplier._id !== action.payload);
    },
    addCustomer: (state, action) => {
      state.customers.push(filterCustomerData([action.payload])[0]);
    },
    updateCustomer: (state, action) => {
      const index = state.customers.findIndex(customer => 
        (customer._id || customer.id) === (action.payload._id || action.payload.id)
      );
      if (index !== -1) {
        state.customers[index] = filterCustomerData([action.payload])[0];
      }
    },
    removeCustomer: (state, action) => {
      state.customers = state.customers.filter(customer => 
        (customer._id || customer.id) !== action.payload
      );
    },
    setCustomers: (state, action) => {
      // Remove duplicates when setting customers
      const uniqueCustomers = removeDuplicates(action.payload);
      state.customers = uniqueCustomers;
    },
    addEmployee: (state, action) => {
      state.employees.push(filterEmployeeData([action.payload])[0]);
    },
    updateEmployee: (state, action) => {
      const index = state.employees.findIndex(employee => employee._id === action.payload._id);
      if (index !== -1) {
        state.employees[index] = filterEmployeeData([action.payload])[0];
      }
    },
    removeEmployee: (state, action) => {
      state.employees = state.employees.filter(employee => employee._id !== action.payload);
    },
    // Payment Methods actions
    addPaymentMethod: (state, action) => {
      state.paymentMethods.push(action.payload);
    },
    updatePaymentMethod: (state, action) => {
      const index = state.paymentMethods.findIndex(method => {
        const methodId = method._id || method.id;
        const payloadId = action.payload._id || action.payload.id;
        return methodId === payloadId;
      });
      if (index !== -1) {
        state.paymentMethods[index] = action.payload;
      }
    },
    removePaymentMethod: (state, action) => {
      state.paymentMethods = state.paymentMethods.filter(method => {
        const methodId = method._id || method.id;
        return methodId !== action.payload;
      });
    },
    setPaymentMethods: (state, action) => {
      state.paymentMethods = action.payload;
    },
    // Payment Terms actions
    addPaymentTerm: (state, action) => {
      if (!Array.isArray(state.paymentTerms)) {
        state.paymentTerms = [];
      }
      state.paymentTerms.push(action.payload);
    },
    updatePaymentTerm: (state, action) => {
      if (!Array.isArray(state.paymentTerms)) {
        state.paymentTerms = [];
      }
      const index = state.paymentTerms.findIndex(term => {
        const termId = term._id || term.id;
        const payloadId = action.payload._id || action.payload.id;
        return termId === payloadId;
      });
      if (index !== -1) {
        state.paymentTerms[index] = action.payload;
      }
    },
    removePaymentTerm: (state, action) => {
      if (!Array.isArray(state.paymentTerms)) {
        state.paymentTerms = [];
        return;
      }
      state.paymentTerms = state.paymentTerms.filter(term => {
        const termId = term._id || term.id;
        return termId !== action.payload;
      });
    },
    setPaymentTerms: (state, action) => {
      state.paymentTerms = Array.isArray(action.payload) ? action.payload : [];
    },
    // Category actions
    setCategories: (state, action) => {
      state.categories = Array.isArray(action.payload) ? action.payload : [];
    },
    setCategoryTree: (state, action) => {
      state.categoryTree = Array.isArray(action.payload) ? action.payload : [];
    },
    addCategory: (state, action) => {
      if (!Array.isArray(state.categories)) {
        state.categories = [];
      }
      state.categories.push(action.payload);
    },
    updateCategory: (state, action) => {
      if (!Array.isArray(state.categories)) {
        state.categories = [];
      }
      const index = state.categories.findIndex(cat => {
        const catId = cat._id || cat.id;
        const payloadId = action.payload._id || action.payload.id;
        return catId === payloadId;
      });
      if (index !== -1) {
        state.categories[index] = action.payload;
      }
    },
    removeCategory: (state, action) => {
      if (!Array.isArray(state.categories)) {
        state.categories = [];
        return;
      }
      state.categories = state.categories.filter(cat => {
        const catId = cat._id || cat.id;
        return catId !== action.payload;
      });
    },
    // Item Sets actions
    setItemSets: (state, action) => {
      state.itemSets = Array.isArray(action.payload) ? action.payload : [];
    },
    addItemSet: (state, action) => {
      if (!Array.isArray(state.itemSets)) {
        state.itemSets = [];
      }
      state.itemSets.push(action.payload);
    },
    updateItemSet: (state, action) => {
      if (!Array.isArray(state.itemSets)) {
        state.itemSets = [];
      }
      const index = state.itemSets.findIndex(set => {
        const setId = set._id || set.id;
        const payloadId = action.payload._id || action.payload.id;
        return setId === payloadId;
      });
      if (index !== -1) {
        state.itemSets[index] = action.payload;
      }
    },
    removeItemSet: (state, action) => {
      if (!Array.isArray(state.itemSets)) {
        state.itemSets = [];
        return;
      }
      state.itemSets = state.itemSets.filter(set => {
        const setId = set._id || set.id;
        return setId !== action.payload;
      });
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMasterData.fulfilled, (state, action) => {
        state.loading = false;
        state.lastFetchedAt = Date.now();
        state.items = action.payload.items;
        state.suppliers = action.payload.suppliers;
        state.customers = action.payload.customers;
        state.employees = action.payload.employees;
        state.paymentMethods = action.payload.paymentMethods;
        state.paymentTerms = Array.isArray(action.payload.paymentTerms) ? action.payload.paymentTerms : [];
        state.categories = Array.isArray(action.payload.categories) ? action.payload.categories : [];
        state.categoryTree = Array.isArray(action.payload.categoryTree) ? action.payload.categoryTree : [];
        state.itemSets = Array.isArray(action.payload.itemSets) ? action.payload.itemSets : [];
        state.error = null;
      })
      .addCase(fetchMasterData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  clearMasterData,
  addItem,
  updateItem,
  setItems,
  removeItem,
  addSupplier,
  updateSupplier,
  removeSupplier,
  addCustomer,
  updateCustomer,
  removeCustomer,
  setCustomers,
  addEmployee,
  updateEmployee,
  removeEmployee,
  addPaymentMethod,
  updatePaymentMethod,
  removePaymentMethod,
  setPaymentMethods,
  setCategories,
  setCategoryTree,
  addCategory,
  updateCategory,
  removeCategory,
  addPaymentTerm,
  updatePaymentTerm,
  removePaymentTerm,
  setPaymentTerms,
  setItemSets,
  addItemSet,
  updateItemSet,
  removeItemSet
} = masterDataSlice.actions;

export default masterDataSlice.reducer; 